/**
 * Utilities for extracting domain entities from SearchResult wrappers.
 *
 * After the search.proto refactoring, all search results are wrapped in SearchResult
 * messages with a oneof field for the specific domain entity (place, hotel, etc.).
 * These utilities handle the extraction and type narrowing.
 */

import type {
  Place,
  Hotel,
  SearchResult,
  SearchSession,
  ActivityResult,
  RestaurantResult,
  TransportationResult
} from '@generated/proto/model'
import type { SearchPlacesResponse } from '@generated/proto/service'

/**
 * Extract places from SearchResult array.
 * Filters out undefined values and provides proper type narrowing.
 */
export function extractPlaces(results: SearchResult[] | undefined): Place[] {
  if (!results) return []

  return results
    .map(r => r.place?.place)
    .filter((place): place is Place => place !== undefined)
}

/**
 * Extract places from a SearchSession response.
 * Convenience wrapper for the common pattern of extracting from session.results.
 */
export function extractPlacesFromSession(session: SearchSession | undefined): Place[] {
  return extractPlaces(session?.results)
}

/**
 * Extract places from a SearchPlacesResponse.
 * Convenience wrapper that delegates to extractPlacesFromSession.
 */
export function extractPlacesFromResult(response: SearchPlacesResponse | undefined): Place[] {
  return extractPlacesFromSession(response?.searchSession)
}

/**
 * Extract hotels from SearchResult array.
 * TODO: Implement once hotel search is using SearchSession pattern.
 */
export function extractHotels(results: SearchResult[] | undefined): Hotel[] {
  if (!results) return []

  return results
    .map(r => r.hotel?.hotel)
    .filter((hotel): hotel is Hotel => hotel !== undefined)
}

/**
 * Extract hotels from a SearchSession response.
 */
export function extractHotelsFromSession(session: SearchSession | undefined): Hotel[] {
  return extractHotels(session?.results)
}

/**
 * Visitor interface for processing different result types.
 * Use this pattern when you need to handle multiple result types in a type-safe way.
 *
 * @example
 * ```typescript
 * const visitor: SearchResultVisitor<string> = {
 *   place: (place) => `Place: ${place.name}`,
 *   hotel: (hotel) => `Hotel: ${hotel.name}`,
 *   activity: (activity) => `Activity: ${activity.name}`,
 *   restaurant: (restaurant) => `Restaurant: ${restaurant.name}`,
 *   transportation: (transportation) => `Flight: ${transportation.itinerary?.legs?.[0]?.carrier}`,
 * }
 *
 * const descriptions = results.map(r => visitSearchResult(r, visitor))
 * ```
 */
export interface SearchResultVisitor<T> {
  place?: (place: Place, result: SearchResult) => T
  hotel?: (hotel: Hotel, result: SearchResult) => T
  activity?: (activity: ActivityResult, result: SearchResult) => T
  restaurant?: (restaurant: RestaurantResult, result: SearchResult) => T
  transportation?: (transportation: TransportationResult, result: SearchResult) => T
}

/**
 * Visit a SearchResult with a type-safe visitor pattern.
 * Returns undefined if the result type doesn't match any visitor handlers.
 */
export function visitSearchResult<T>(
  result: SearchResult,
  visitor: SearchResultVisitor<T>
): T | undefined {
  // Check each result type and call the corresponding visitor
  if (result.place?.place && visitor.place) {
    return visitor.place(result.place.place, result)
  }

  if (result.hotel?.hotel && visitor.hotel) {
    return visitor.hotel(result.hotel.hotel, result)
  }

  if (result.activity && visitor.activity) {
    return visitor.activity(result.activity, result)
  }

  if (result.restaurant && visitor.restaurant) {
    return visitor.restaurant(result.restaurant, result)
  }

  if (result.transportation && visitor.transportation) {
    return visitor.transportation(result.transportation, result)
  }

  return undefined
}

/**
 * Visit all SearchResults with a visitor pattern, filtering out undefined results.
 */
export function visitSearchResults<T>(
  results: SearchResult[] | undefined,
  visitor: SearchResultVisitor<T>
): T[] {
  if (!results) return []

  return results
    .map(r => visitSearchResult(r, visitor))
    .filter((value): value is T => value !== undefined)
}

/**
 * Get the primary display name for any search result type.
 * Useful for generic UI components that display mixed result types.
 */
export function getSearchResultName(result: SearchResult): string | undefined {
  if (result.place?.place) return result.place.place.name
  if (result.hotel?.hotel) return result.hotel.hotel.name
  if (result.activity?.activity) return result.activity.activity?.name
  if (result.restaurant?.restaurant) return result.restaurant.restaurant?.name
  if (result.transportation?.itinerary) {
    const firstLeg = result.transportation.itinerary.legs?.[0]
    if (firstLeg) {
      return `${firstLeg.origin?.code} → ${firstLeg.destination?.code}`
    }
  }

  return undefined
}

/**
 * Get the result type as a string for debugging or display.
 */
export function getSearchResultType(result: SearchResult): string {
  if (result.place) return 'place'
  if (result.hotel) return 'hotel'
  if (result.activity) return 'activity'
  if (result.restaurant) return 'restaurant'
  if (result.transportation) return 'transportation'
  return 'unknown'
}
